import { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Dialog, IconButton, TabBar, SearchInput, Loader, Notice } from '@origo/origo-ui';
import { ArrowsOutIcon } from '@phosphor-icons/react';
import DOMPurify from 'dompurify';
import type { GalleryItem } from './types';
import { useKommunaltGalleriService } from '../../../../servicecontexts/KommunaltGalleriServiceContext';
import type { KommunaltGalleriItem } from '../../services/kommunaltGalleriService';
import { galleriBildeUrl, useSentraltGalleri, type GalleriElement } from '@origo/galleri';

function mapSentraltToGalleryItem(item: GalleriElement): GalleryItem {
    return {
        id: item.id,
        filId: null,
        galleriElementId: item.id,
        kommunenummer: item.kommunenummer,
        tittel: item.tittel ?? '',
        beskrivelse: item.beskrivelse || null,
        prioritet: item.prioritet,
        uri: item.bildeUrl ? galleriBildeUrl(item.id, item.oppdatert) : null,
    };
}

function mapToGalleryItem(item: KommunaltGalleriItem, index: number): GalleryItem {
    return {
        id: item.objectId,
        filId: null,
        kommunenummer: null,
        tittel: item.tittel,
        beskrivelse: item.beskrivelse,
        prioritet: index,
        uri: item.bilde_url,
        persistUri: item.bilde_permanent_url,
        source: 'kommunalt',
    };
}
import styles from './GallerySelectionModal.module.css';

interface GallerySelectionModalProps {
    open: boolean;
    onClose: () => void;
    onSelectItems: (items: GalleryItem[]) => Promise<void>;
    contextLabel?: string;
}

const TABS = [
    { id: 'sentralt', label: 'Sentralt galleri' },
    { id: 'kommunalt', label: 'Kommunalt galleri' },
];

export function GallerySelectionModal({
    open,
    onClose,
    onSelectItems,
    contextLabel,
}: GallerySelectionModalProps) {
    const service = useKommunaltGalleriService();
    const sentraltQuery = useSentraltGalleri(open);
    const sentraltItems = useMemo(
        () => (sentraltQuery.data ?? []).map(mapSentraltToGalleryItem),
        [sentraltQuery.data],
    );
    const sentraltLoading = sentraltQuery.isPending && open;
    const dialogRef = useRef<HTMLDialogElement>(null);
    const [activeTab, setActiveTab] = useState('sentralt');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedItem, setExpandedItem] = useState<GalleryItem | null>(null);
    const [kommunaltItems, setKommunaltItems] = useState<GalleryItem[]>([]);
    const [kommunaltLoading, setKommunaltLoading] = useState(false);
    const [kommunaltError, setKommunaltError] = useState<string | null>(null);
    const [kommunaltFetched, setKommunaltFetched] = useState(false);

    // Sync dialog open/close
    useEffect(() => {
        const el = dialogRef.current;
        if (!el) return;
        if (open) {
            if (!el.open) el.showModal();
        } else {
            if (el.open) el.close();
        }
    }, [open]);

    // Reset state on open
    useEffect(() => {
        if (open) {
            setSelected(new Set());
            setActiveTab('sentralt');
            setSearchQuery('');
            setKommunaltItems([]);
            setKommunaltFetched(false);
            setKommunaltError(null);
        }
    }, [open]);

    // Fetch kommunalt galleri lazily when the tab is first activated
    useEffect(() => {
        if (activeTab !== 'kommunalt' || kommunaltFetched || !open) return;
        if (!service.isConfigured) {
            setKommunaltError('Kommunalt galleri er ikke tilgjengelig.');
            setKommunaltFetched(true);
            return;
        }
        let cancelled = false;
        setKommunaltLoading(true);
        setKommunaltError(null);
        service.fetchKommunaltGalleri()
            .then(items => {
                if (cancelled) return;
                setKommunaltItems(items.map(mapToGalleryItem));
                setKommunaltFetched(true);
            })
            .catch(() => {
                if (!cancelled) setKommunaltError('Kunne ikke laste kommunalt galleri.');
            })
            .finally(() => {
                if (!cancelled) setKommunaltLoading(false);
            });
        return () => { cancelled = true; };
    }, [activeTab, kommunaltFetched, open, service]);

    // Prevent ESC from closing
    useEffect(() => {
        const el = dialogRef.current;
        if (!el) return;
        function handleCancel(e: Event) {
            e.preventDefault();
            onClose();
        }
        el.addEventListener('cancel', handleCancel);
        return () => el.removeEventListener('cancel', handleCancel);
    });

    const displayItems = activeTab === 'kommunalt' ? kommunaltItems : sentraltItems;
    const sortedItems = [...displayItems]
        .sort((a, b) => a.prioritet - b.prioritet);

    const query = searchQuery.trim().toLowerCase();
    const filteredItems = query
        ? sortedItems.filter(item => item.tittel.toLowerCase().includes(query))
        : sortedItems;

    const allItems = [...sentraltItems, ...kommunaltItems];

    function handleTabChange(tab: string) {
        setActiveTab(tab);
        setSearchQuery('');
    }

    function itemKey(item: GalleryItem) {
        return `${item.source ?? 'sentralt'}-${item.id}`;
    }

    function toggleItem(item: GalleryItem) {
        setSelected(prev => {
            const next = new Set(prev);
            const key = itemKey(item);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }

    async function handleLeggTil() {
        const chosenItems = allItems.filter(item => selected.has(itemKey(item)));
        setSaving(true);
        try {
            await onSelectItems(chosenItems);
        } finally {
            setSaving(false);
        }
    }

    const selectedCount = selected.size;

    return (
        <>
            <dialog ref={dialogRef} className={styles.page}>
                <header className={styles.header}>
                    <h1 className={styles.headerTitle}>Velg illustrasjoner</h1>
                    {contextLabel && (
                        <span className={styles.headerContext}>{contextLabel}</span>
                    )}
                    <span className={styles.headerCount}>
                        {query ? `${filteredItems.length} av ${sortedItems.length}` : `${sortedItems.length} tilgjengelig`}
                    </span>
                </header>

                <div className={styles.body}>
                    <TabBar
                        tabs={TABS}
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                    />
                    <div
                        className={styles.tabContent}
                        role="tabpanel"
                        id={`tabpanel-${activeTab}`}
                        aria-labelledby={`tab-${activeTab}`}
                    >
                        <div className={styles.searchBar}>
                            <SearchInput
                                label="Søk"
                                hideLabel
                                placeholder="Filtrer etter navn..."
                                value={searchQuery}
                                onChange={setSearchQuery}
                            />
                        </div>
                        <div className={styles.grid}>
                            {sentraltLoading && activeTab === 'sentralt' && (
                                <Loader />
                            )}
                            {sentraltQuery.isError && activeTab === 'sentralt' && (
                                <Notice variant="danger">Kunne ikke laste sentralt galleri.</Notice>
                            )}
                            {kommunaltLoading && activeTab === 'kommunalt' && (
                                <Loader />
                            )}
                            {kommunaltError && activeTab === 'kommunalt' && (
                                <Notice variant="danger">{kommunaltError}</Notice>
                            )}
                            {filteredItems.length === 0 && !(activeTab === 'sentralt' ? sentraltLoading || sentraltQuery.isError : kommunaltLoading || kommunaltError) && (
                                <p className={styles.emptyMessage}>Ingen illustrasjoner samsvarer med søket.</p>
                            )}
                            {filteredItems.map(item => (
                                <div
                                    key={item.id}
                                    role="checkbox"
                                    aria-checked={selected.has(itemKey(item))}
                                    tabIndex={0}
                                    className={`${styles.card} ${selected.has(itemKey(item)) ? styles.selected : ''}`}
                                    onClick={() => toggleItem(item)}
                                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleItem(item); } }}
                                >
                                    {/* Elementer uten bilde er rene tekstelementer — ingen bildeflate. */}
                                    {item.uri && (
                                        <div className={styles.cardImage}>
                                            <img src={item.uri} alt={item.tittel} loading="lazy" />
                                        </div>
                                    )}
                                    <div className={styles.cardBody}>
                                        <div className={styles.cardTitleRow}>
                                            <p className={styles.cardTitle}>{item.tittel}</p>
                                            <IconButton
                                                icon={<ArrowsOutIcon size={14} />}
                                                label="Forhåndsvis"
                                                title="Forhåndsvis"
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => { e.stopPropagation(); setExpandedItem(item); }}
                                            />
                                        </div>
                                        {item.beskrivelse && (
                                            <p
                                                className={styles.cardDescription}
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(item.beskrivelse)
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <footer className={styles.footer}>
                    <span className={styles.footerCount}>
                        {selectedCount > 0 ? `${selectedCount} valgt` : ''}
                    </span>
                    <div className={styles.footerActions}>
                        <Button variant="secondary" onClick={onClose} disabled={saving}>
                            Avbryt
                        </Button>
                        <Button
                            onClick={handleLeggTil}
                            disabled={selectedCount === 0 || saving}
                        >
                            {selectedCount > 0
                                ? `Legg til ${selectedCount} illustrasjon${selectedCount !== 1 ? 'er' : ''}`
                                : 'Legg til illustrasjoner'}
                        </Button>
                    </div>
                </footer>
            </dialog>

            <Dialog
                open={expandedItem !== null}
                onClose={() => setExpandedItem(null)}
                title={expandedItem?.tittel ?? ''}
                className={styles.detailDialog}
                footer={
                    expandedItem && (
                        <Button
                            variant={selected.has(itemKey(expandedItem)) ? 'secondary' : 'primary'}
                            onClick={() => { toggleItem(expandedItem); setExpandedItem(null); }}
                        >
                            {selected.has(itemKey(expandedItem)) ? 'Fjern valg' : 'Velg illustrasjon'}
                        </Button>
                    )
                }
            >
                {expandedItem && (
                    <>
                        {expandedItem.uri && (
                            <div className={styles.detailImage}>
                                <img src={expandedItem.uri} alt={expandedItem.tittel} />
                            </div>
                        )}
                        {expandedItem.beskrivelse && (
                            <div
                                className={styles.detailDescription}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(expandedItem.beskrivelse)
                                }}
                            />
                        )}
                    </>
                )}
            </Dialog>
        </>
    );
}
